import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { DateTimePicker } from '@/components/DateTimePicker';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface Agency {
    uuid: string;
    id: any;
    agency: string;
    people: string;
    arrived: Date | null;
    notes: string;
    [key: string]: any;
}

interface EditingField {
    uuid: string;
    field: string;
}

interface DeleteConfirmState {
    open: boolean;
    uuid: string | null;
    name: string;
}

interface FieldInfo {
    label: string;
    field: string;
    type: 'text' | 'textarea' | 'datetime';
}

export interface CaseReportRespondingAgenciesProps {
    agencies: Agency[];
    title?: string;
    onChange?: (agencies: Agency[]) => void;
}

export function CaseReportRespondingAgencies({
    agencies,
    title = 'Responding Agencies',
    onChange,
}: CaseReportRespondingAgenciesProps) {
    const { theme } = useTheme();

    // State
    const [editingField, setEditingField] = useState<EditingField | null>(null);
    const [editValue, setEditValue] = useState<any>('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerOpenDesktop, setDatePickerOpenDesktop] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isTabbing, setIsTabbing] = useState(false);
    const [isJustStartingEdit, setIsJustStartingEdit] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
        open: false,
        uuid: null,
        name: '',
    });

    // Refs
    const rootRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const editingFieldRef = useRef(editingField);
    const editValueRef = useRef(editValue);
    const isTabbingRef = useRef(isTabbing);
    const isJustStartingEditRef = useRef(isJustStartingEdit);
    const agenciesRef = useRef(agencies);
    const pendingEditActionRef = useRef<string | null>(null);
    const shouldScrollToLastRef = useRef(false);

    // Keep refs in sync
    useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
    useEffect(() => { editValueRef.current = editValue; }, [editValue]);
    useEffect(() => { isTabbingRef.current = isTabbing; }, [isTabbing]);
    useEffect(() => { isJustStartingEditRef.current = isJustStartingEdit; }, [isJustStartingEdit]);
    useEffect(() => { agenciesRef.current = agencies; }, [agencies]);

    const fields: FieldInfo[] = useMemo(() => [
        { label: 'Agency', field: 'agency', type: 'text' },
        { label: 'Person(s)', field: 'people', type: 'textarea' },
        { label: 'Arrived', field: 'arrived', type: 'datetime' },
        { label: 'Notes', field: 'notes', type: 'textarea' },
    ], []);

    const datePickerPopupClass = useMemo(() => theme || '', [theme]);

    // Helpers
    const emitReplace = useCallback((newArr: Agency[]) => {
        onChange?.(newArr);
    }, [onChange]);

    // Mobile check
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const checkIfMobile = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
        };
        setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', checkIfMobile);
        return () => {
            window.removeEventListener('resize', checkIfMobile);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // After editingField renders, perform any pending action
    useEffect(() => {
        if (!editingField || !pendingEditActionRef.current) return;
        const action = pendingEditActionRef.current;
        pendingEditActionRef.current = null;

        if (action === 'datePickerDesktop') {
            setDatePickerOpenDesktop(true);
        } else if (action === 'datePickerMobile') {
            setDatePickerOpen(true);
            setTimeout(() => {
                setIsJustStartingEdit(false);
                isJustStartingEditRef.current = false;
            }, 100);
        }
    }, [editingField]);

    // After agencies array changes, scroll to last card if requested
    useEffect(() => {
        if (shouldScrollToLastRef.current) {
            shouldScrollToLastRef.current = false;
            if (rootRef.current) scrollToLastMobileCard(rootRef.current);
        }
    }, [agencies]);

    // Cleanup close timeout on unmount
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    // Editing helpers
    const isEditing = useCallback((rowUuid: string, field: string) => {
        return editingField?.uuid === rowUuid && editingField?.field === field;
    }, [editingField]);

    const getNextEditableField = useCallback((currentUuid: string, currentField: string): EditingField | null => {
        const editableFields = ['agency', 'people', 'arrived', 'notes'];
        const currentAgencyIndex = agenciesRef.current.findIndex((a) => a.uuid === currentUuid);
        const currentFieldIndex = editableFields.indexOf(currentField);

        if (currentFieldIndex < editableFields.length - 1) {
            return { uuid: currentUuid, field: editableFields[currentFieldIndex + 1] };
        }

        if (currentAgencyIndex < agenciesRef.current.length - 1) {
            const nextAgency = agenciesRef.current[currentAgencyIndex + 1];
            return { uuid: nextAgency.uuid, field: editableFields[0] };
        }

        return null;
    }, []);

    const getPreviousEditableField = useCallback((currentUuid: string, currentField: string): EditingField | null => {
        const editableFields = ['agency', 'people', 'arrived', 'notes'];
        const currentAgencyIndex = agenciesRef.current.findIndex((a) => a.uuid === currentUuid);
        const currentFieldIndex = editableFields.indexOf(currentField);

        if (currentFieldIndex > 0) {
            return { uuid: currentUuid, field: editableFields[currentFieldIndex - 1] };
        }

        if (currentAgencyIndex > 0) {
            const prevAgency = agenciesRef.current[currentAgencyIndex - 1];
            return { uuid: prevAgency.uuid, field: editableFields[editableFields.length - 1] };
        }

        return null;
    }, []);

    const closeEditMode = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setEditingField(null);
        setEditValue('');
        setDatePickerOpen(false);
        setDatePickerOpenDesktop(false);
    }, []);

    const saveEdit = useCallback(() => {
        if (!editingFieldRef.current) return;
        if (isJustStartingEditRef.current) return;

        const { uuid, field } = editingFieldRef.current;
        const currentEditValue = editValueRef.current;

        emitReplace(agenciesRef.current.map((a) =>
            a.uuid === uuid ? { ...a, [field]: currentEditValue } : a
        ));
    }, [emitReplace]);

    const cancelEdit = useCallback(() => {
        closeEditMode();
    }, [closeEditMode]);

    const focusInput = useCallback(() => {
        setTimeout(() => {
            if (!rootRef.current) return;
            const editContainer = rootRef.current.querySelector('.edit-container, .edit-field-mobile');
            if (editContainer) {
                const input = editContainer.querySelector('.edit-input:not([type="hidden"])') as HTMLInputElement;
                if (input) {
                    if (isMobile) {
                        setTimeout(() => {
                            input.focus();
                            input.click();
                        }, 150);
                    } else {
                        input.focus();
                        if (input.type === 'text') {
                            input.select();
                        }
                    }
                }
            }
        }, 0);
    }, [isMobile]);

    const startEdit = useCallback((rowUuid: string, field: string, currentValue: any) => {
        // Save any existing edit first
        if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
            saveEdit();
            closeEditMode();
        }

        setEditingField({ uuid: rowUuid, field });
        if (field === 'arrived') {
            setEditValue(currentValue !== null ? currentValue : new Date());
            pendingEditActionRef.current = 'datePickerDesktop';
        } else {
            setEditValue(currentValue || '');
            focusInput();
        }
    }, [saveEdit, closeEditMode, focusInput]);

    const startEditMobile = useCallback((rowUuid: string, field: string, currentValue: any) => {
        // Save any existing edit first
        if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
            saveEdit();
            closeEditMode();
        }

        setIsJustStartingEdit(true);
        isJustStartingEditRef.current = true;

        setEditingField({ uuid: rowUuid, field });
        if (field === 'arrived') {
            setEditValue(currentValue ?? new Date());
            pendingEditActionRef.current = 'datePickerMobile';
        } else {
            setEditValue(currentValue || '');
            setTimeout(() => {
                setIsJustStartingEdit(false);
                isJustStartingEditRef.current = false;
            }, 200);
        }
    }, [saveEdit, closeEditMode]);

    const handleBlur = useCallback(() => {
        saveEdit();
        closeEditMode();
    }, [saveEdit, closeEditMode]);

    const clearDate = useCallback(() => {
        setEditValue(null);
        // Save with null then close
        setTimeout(() => {
            const ef = editingFieldRef.current;
            if (!ef) return;
            const { uuid, field } = ef;
            emitReplace(agenciesRef.current.map((a) =>
                a.uuid === uuid ? { ...a, [field]: null } : a
            ));
            closeEditMode();
        }, 0);
    }, [emitReplace, closeEditMode]);

    const handleDateChange = useCallback((v: Date | null) => {
        const newVal = (v === null || v === undefined) ? null : v;
        editValueRef.current = newVal;
        setEditValue(newVal);
    }, []);

    const handleTabKey = useCallback((event: React.KeyboardEvent) => {
        if (!editingFieldRef.current) return;

        event.preventDefault();
        event.stopPropagation();

        setIsTabbing(true);
        isTabbingRef.current = true;
        const currentField = { ...editingFieldRef.current };
        const isShiftPressed = event.shiftKey;

        saveEdit();
        closeEditMode();

        setTimeout(() => {
            const next = isShiftPressed
                ? getPreviousEditableField(currentField.uuid, currentField.field)
                : getNextEditableField(currentField.uuid, currentField.field);

            if (next) {
                const agency = agenciesRef.current.find((a) => a.uuid === next.uuid);
                if (agency) {
                    startEdit(next.uuid, next.field, agency[next.field]);
                }
            }

            setIsTabbing(false);
            isTabbingRef.current = false;
        }, 0);
    }, [saveEdit, closeEditMode, getNextEditableField, getPreviousEditableField, startEdit]);

    const handleDatePickerClose = useCallback(() => {
        setDatePickerOpenDesktop(false);

        if (isTabbingRef.current) return;

        saveEdit();

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            closeEditMode();
        }, 150);
    }, [saveEdit, closeEditMode]);

    const handleDatePickerCloseMobile = useCallback(() => {
        setDatePickerOpen(false);

        if (isTabbingRef.current) return;

        saveEdit();

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            closeEditMode();
        }, 150);
    }, [saveEdit, closeEditMode]);

    // Add / delete
    const addAgency = useCallback(() => {
        const newAgency: Agency = {
            uuid: getInsecureUUID(),
            id: null,
            agency: '',
            people: '',
            arrived: new Date(),
            notes: '',
        };
        emitReplace([...agenciesRef.current, newAgency]);
        shouldScrollToLastRef.current = true;
    }, [emitReplace]);

    const openDeleteConfirm = useCallback((uuid: string) => {
        const row = agenciesRef.current.find((a) => a.uuid === uuid);
        setDeleteConfirm({
            open: true,
            uuid,
            name: row?.agency || 'this agency',
        });
    }, []);

    const cancelDeleteConfirm = useCallback(() => {
        setDeleteConfirm({ open: false, uuid: null, name: '' });
    }, []);

    const confirmDelete = useCallback(() => {
        const uuid = deleteConfirm.uuid;
        if (!uuid) { cancelDeleteConfirm(); return; }
        emitReplace(agenciesRef.current.filter((a) => a.uuid !== uuid));
        cancelDeleteConfirm();
    }, [deleteConfirm.uuid, emitReplace, cancelDeleteConfirm]);

    // Format helpers
    const formatDate = useCallback((date: any) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }, []);

    const formatTime = useCallback((date: any) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }, []);

    // Render helpers for desktop table cells
    const renderDesktopTextCell = (agency: Agency, field: string, isTextarea: boolean) => {
        const fieldValue = agency[field];

        if (isEditing(agency.uuid, field)) {
            return (
                <div className="edit-container">
                    {isTextarea ? (
                        <textarea
                            className="edit-input edit-textarea"
                            value={editValue}
                            rows={3}
                            autoFocus
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleBlur}
                            onKeyUp={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                            onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                    ) : (
                        <input
                            className="edit-input"
                            value={editValue}
                            autoFocus
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleBlur}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter') handleBlur();
                                if (e.key === 'Escape') cancelEdit();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                    )}
                </div>
            );
        }

        const isMultiline = isTextarea;
        return (
            <div
                className={`editable${isMultiline ? ' multiline' : ''}`}
                onClick={(e) => { e.stopPropagation(); startEdit(agency.uuid, field, fieldValue); }}
            >
                {isMultiline ? (
                    <span className="multiline-text">{fieldValue || '\u2014'}</span>
                ) : (
                    fieldValue || '\u2014'
                )}
            </div>
        );
    };

    const renderDesktopDateCell = (agency: Agency) => {
        if (isEditing(agency.uuid, 'arrived')) {
            return (
                <div className="edit-datetime">
                    <div className="datetime-edit-controls">
                        <DateTimePicker
                            value={editValue}
                            type="datetime"
                            format="DD MMM YYYY HH:mm:ss"
                            showSecond={true}
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpenDesktop}
                            onChange={handleDateChange}
                            onConfirm={saveEdit}
                            onClear={clearDate}
                            onClose={handleDatePickerClose}
                            onKeyDown={handleTabKey}
                        />
                        <button className="btn-clear-date" onClick={clearDate} title="Clear date/time">
                            <i className="fas fa-times-circle" />
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div
                className="editable"
                onClick={(e) => { e.stopPropagation(); startEdit(agency.uuid, 'arrived', agency.arrived); }}
            >
                {agency.arrived ? (
                    <>
                        <div className="datetime-display">{formatDate(agency.arrived)}</div>
                        <div className="time-display">{formatTime(agency.arrived)}</div>
                    </>
                ) : (
                    <div className="empty-cell">&mdash;</div>
                )}
            </div>
        );
    };

    // Render helpers for mobile card fields
    const renderMobileFieldContent = (agency: Agency, fieldInfo: FieldInfo) => {
        const fieldValue = agency[fieldInfo.field];

        if (isEditing(agency.uuid, fieldInfo.field)) {
            if (fieldInfo.type === 'text') {
                return (
                    <input
                        className="edit-input"
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyUp={(e) => { if (e.key === 'Enter') handleBlur(); }}
                    />
                );
            }
            if (fieldInfo.type === 'textarea') {
                return (
                    <textarea
                        className="edit-input edit-textarea"
                        value={editValue}
                        rows={3}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleBlur}
                    />
                );
            }
            if (fieldInfo.type === 'datetime') {
                return (
                    <div className="datetime-edit-controls-mobile">
                        <DateTimePicker
                            value={editValue}
                            type="datetime"
                            format="DD MMM YYYY HH:mm:ss"
                            showSecond={true}
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpen}
                            onChange={handleDateChange}
                            onConfirm={saveEdit}
                            onClear={clearDate}
                            onClose={handleDatePickerCloseMobile}
                            inputAttr={{ readOnly: isMobile }}
                            onKeyDown={handleTabKey}
                        />
                        <button className="btn-clear-date-mobile" onClick={clearDate} title="Clear date/time">
                            <i className="fas fa-times-circle" />
                        </button>
                    </div>
                );
            }
        }

        // Display mode
        return (
            <div
                className="card-field-value"
                onClick={() => startEditMobile(agency.uuid, fieldInfo.field, fieldValue)}
            >
                {fieldInfo.type === 'datetime' ? (
                    fieldValue ? (
                        <>
                            <div className="datetime-display">{formatDate(fieldValue)}</div>
                            <div className="time-display">{formatTime(fieldValue)}</div>
                        </>
                    ) : (
                        <div className="empty-cell">&mdash;</div>
                    )
                ) : fieldInfo.type === 'textarea' ? (
                    <span className="multiline-text">{fieldValue || '\u2014'}</span>
                ) : (
                    fieldValue || '\u2014'
                )}
            </div>
        );
    };

    return (
        <div className={`responding-agencies${theme ? ' ' + theme : ''}`} ref={rootRef}>
            <div className="header">
                <h1>{title}</h1>
                <button className="btn-add" onClick={addAgency}>
                    <i className="fas fa-plus" /> AGENCY
                </button>
            </div>

            {/* Desktop table */}
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Agency</th>
                            <th style={{ width: '20%' }}>Person(s)</th>
                            <th style={{ width: '20%' }}>Arrived</th>
                            <th style={{ width: '32%' }}>Notes</th>
                            <th style={{ width: '8%' }}>Actions</th>
                        </tr>
                    </thead>
                    {agencies.length === 0 ? (
                        <tbody>
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    No agencies assigned
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {agencies.map((agency) => (
                                <tr key={agency.uuid}>
                                    {/* Agency name */}
                                    <td>
                                        {renderDesktopTextCell(agency, 'agency', false)}
                                    </td>

                                    {/* People */}
                                    <td>
                                        {renderDesktopTextCell(agency, 'people', true)}
                                    </td>

                                    {/* Arrived */}
                                    <td>
                                        {renderDesktopDateCell(agency)}
                                    </td>

                                    {/* Notes */}
                                    <td>
                                        {renderDesktopTextCell(agency, 'notes', true)}
                                    </td>

                                    {/* Actions */}
                                    <td>
                                        <div className="actions">
                                            <button
                                                className="btn-delete"
                                                onClick={() => openDeleteConfirm(agency.uuid)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            {/* Mobile cards */}
            <div className="mobile-cards">
                {agencies.length === 0 ? (
                    <div className="empty-state">
                        <p>No agencies assigned</p>
                    </div>
                ) : (
                    agencies.map((agency) => (
                        <div key={'mobile-' + agency.uuid} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    <div className="card-agency-name">
                                        {agency.agency || 'Agency'}
                                    </div>
                                </div>
                                <button className="btn-delete" onClick={() => openDeleteConfirm(agency.uuid)}>
                                    Delete
                                </button>
                            </div>

                            <div className="card-grid">
                                {fields.map((fieldInfo) => (
                                    <div key={fieldInfo.field} className="card-field">
                                        <div className="card-field-label">{fieldInfo.label}</div>
                                        {renderMobileFieldContent(agency, fieldInfo)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Delete confirmation modal */}
            <ConfirmModal
                open={deleteConfirm.open}
                title={`Remove ${deleteConfirm.name}?`}
                confirmText="Remove"
                cancelText="Cancel"
                autoFocus="cancel"
                scrollLock={true}
                theme={theme}
                onConfirm={confirmDelete}
                onCancel={cancelDeleteConfirm}
            />
        </div>
    );
}

(window as any).CaseReportRespondingAgenciesReact = CaseReportRespondingAgencies;
